import { QuestionMarkCircleIcon } from "@heroicons/react/24/outline";
import React, { useEffect, useState } from "react";
import { KeywordModalState } from "../../api/Blueprints";
import Button, {
  ButtonColorTypes,
  ButtonTypes,
} from "../../shared-components/Button";
import InputField from "../../shared-components/InputField";
import { classNames } from "../../utils";
import {
  removeNewListDuplicatesAcrossStateLists,
  StateListKeys,
} from "./helpers";
import ReactTooltip from "react-tooltip";

type Props = {
  title: string;
  tooltip: string;
  initialList: string[];
  onChange: (list: string[]) => void;
  onError: (errorMessage: string) => void;
  chipType?: "include" | "exclude";
  state: KeywordModalState;
  stateKey: StateListKeys;
};

function getListWithoutDuplicatesInState(
  newList: string[],
  stateKey: StateListKeys,
  state: KeywordModalState,
  onError: (errorMessage: string) => void
) {
  const newState = { ...state };

  const otherKeys: string[] = Object.keys(state).filter(
    (e) => e !== "name" && e !== stateKey
  );
  const wordInOtherList = newList.some((word: string) =>
    otherKeys.some((key) => newState[key as StateListKeys].includes(word))
  );
  if (wordInOtherList) {
    onError(
      "Some words were not added because they exist as part of another list."
    );
  } else {
    onError("");
  }

  newState[stateKey] = [];
  return removeNewListDuplicatesAcrossStateLists(newList, newState);
}

const KeywordListSection = ({
  title,
  tooltip,
  initialList,
  onChange,
  onError,
  state,
  stateKey,
  chipType = "include",
}: Props) => {
  const [inputText, setInputText] = useState("");
  const [list, setList] = useState<string[]>(initialList);

  useEffect(() => {
    setList(initialList);
  }, [initialList]);

  function handleAdd() {
    const keywordList = inputText
      .split(",")
      .map((name) => name.trim())
      .filter((name) => name)
      .sort((itemA, itemB) => itemA.localeCompare(itemB));
    const newList = Array.from(new Set([...list, ...keywordList]));
    const cleanList = getListWithoutDuplicatesInState(
      newList,
      stateKey,
      state,
      onError
    );
    setList(cleanList);
    setInputText("");
    onChange(cleanList);
  }

  function clear() {
    setList([]);
    onChange([]);
  }

  function handleChipDelete(name: string) {
    const filteredList = list.filter((pivot) => pivot !== name);
    setList(filteredList);
    onChange(filteredList);
  }

  return (
    <div className="w-full border border-solid rounded-md p-2">
      <div className="flex capitalize text-sm mb-2">
        <span>{title} Match</span>
        <span data-tip={tooltip}>
          <QuestionMarkCircleIcon className="w-4 ml-2 text-gray-600" />
        </span>
        <span className="ml-auto text-xs ">
          {list?.length > 0 && (
            <span
              className={classNames(
                "inline-block text-xs rounded-md px-1 mr-2 self-start",
                chipType === "include" ? "bg-green-100" : "bg-yellow-100"
              )}
            >
              {list.length}
            </span>
          )}
          <span onClick={clear} className="underline cursor-pointer">
            clear all
          </span>
        </span>
      </div>
      <div className="flex border border-solid rounded-md items-center mb-2">
        <InputField
          handleChange={(e) => {
            setInputText(e.target.value);
          }}
          handleEnter={handleAdd}
          name={`${title}-input`}
          placeholder="Type here ..."
          classes="w-full"
          className={classNames("mb-0 w-full border-none")}
          value={inputText}
        />
        <Button
          text="Add"
          onClick={handleAdd}
          colorType={ButtonColorTypes.custom}
          type={ButtonTypes.add}
          className="border-l p-0 w-20 bg-gray-50 rounded-l-none"
        />
      </div>
      <div className="border border-solid rounded-md h-20 p-2 overflow-x-auto">
        {list.map((name) => (
          <Keychip
            name={name}
            key={name}
            chipType={chipType}
            onDelete={handleChipDelete}
          />
        ))}
      </div>
      <ReactTooltip
        className="hover:visible:opacity-1 w-60"
        delayHide={1000}
        effect="solid"
        place="right"
        html={true}
        ref={(ref: any) => {
          // Warning: this is a disgusting hack to make the tooltip work in modals :/
          if (!ref) return;
          setTimeout(() => {
            ref?.globalRebuild();
          }, 1000);
        }}
      />
    </div>
  );
};

function Keychip({
  name,
  onDelete,
  chipType,
}: {
  name: string;
  onDelete?: (name: string) => void;
  chipType: "include" | "exclude";
}) {
  return (
    <span
      className={classNames(
        "inline-block text-xs rounded-md px-1 mr-2 self-start",
        chipType === "include" ? "bg-green-100" : "bg-yellow-100"
      )}
    >
      {name}{" "}
      <span
        className="inline-block pl-2 font-bold cursor-pointer"
        onClick={() => onDelete?.(name)}
      >
        x
      </span>
    </span>
  );
}

export default KeywordListSection;
